/**
 * User Activity Tracker
 *
 * Fire-and-forget emission of login / logout / area_visit events.
 * Failures are swallowed — telemetry must never break the app.
 *
 * Coarse-mode rules:
 *   - One login per browser tab/session (sessionStorage flag)
 *   - Area visits are debounced: only emit when the area label changes,
 *     and at most one per 60s per area within a session
 *   - Logout is best-effort; flushed synchronously before redirect
 */

import { isDataverseClientInitialized, onDataverseClientReady } from '@shared/api';
import { userActivityApi } from '../api/userActivityApi';
import type { VeraAppId, CreateUserActivityInput } from '../model/types';

const SESSION_LOGIN_FLAG = 'vera-activity-login-logged';
const SESSION_ID_KEY = 'vera-activity-session-id';
const AREA_THROTTLE_MS = 60_000;

type UserIdentity = {
  oid: string;
  name: string;
  email: string;
};

let currentApp: VeraAppId | null = null;
let currentIdentity: UserIdentity | null = null;
let lastAreaEmit: { area: string; at: number } | null = null;

function getSessionId(): string {
  let id = sessionStorage.getItem(SESSION_ID_KEY);
  if (!id) {
    id =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem(SESSION_ID_KEY, id);
  }
  return id;
}

function currentPath(): string {
  return typeof window !== 'undefined' ? window.location.pathname : '';
}

/**
 * Derive the app id from the current URL so module activity is attributed
 * correctly even when the shell is the only thing that called setApp().
 * Why: the consolidated shell calls setApp('vera-shell') once at module load,
 * but events fired while the user is under /sales/*, /ops/*, /catalog/*
 * should be tagged as that module — otherwise per-app admin views (Sales
 * Manager, etc.) can't filter by app.
 */
function effectiveApp(): VeraAppId | null {
  const path = currentPath();
  if (path.startsWith('/sales')) return 'sales';
  if (path.startsWith('/ops')) return 'ops';
  if (path.startsWith('/catalog')) return 'catalog';
  return currentApp;
}

function currentQuery(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const s = window.location.search || '';
  return s.length > 1 ? s.slice(1) : undefined;
}

// Queue events emitted before the Dataverse client is initialized.
// Why: AuthProvider.checkAuth fires `setIdentity` (which emits a login)
// the instant /auth/me returns, but DataverseOrgProvider only initializes
// the client in a downstream effect — there's a one-render race window.
const pendingEmits: CreateUserActivityInput[] = [];
let readySubscribed = false;

function flushPendingEmits() {
  if (pendingEmits.length === 0) return;
  const queued = pendingEmits.splice(0);
  for (const input of queued) {
    void doEmit(input);
  }
}

async function doEmit(input: CreateUserActivityInput) {
  try {
    await userActivityApi.create(input);
  } catch (err) {
    // Telemetry failures are non-fatal.
    if (typeof console !== 'undefined') console.warn('[user-activity] emit failed', err);
  }
}

async function emit(input: CreateUserActivityInput) {
  if (!isDataverseClientInitialized()) {
    pendingEmits.push(input);
    if (!readySubscribed) {
      readySubscribed = true;
      onDataverseClientReady(flushPendingEmits);
    }
    return;
  }
  await doEmit(input);
}

export const userActivityTracker = {
  /** Called by each app's shell on mount to set the app context. */
  setApp(app: VeraAppId) {
    currentApp = app;
  },

  /** Called by AuthProvider once the user is known. Emits login once per browser session. */
  setIdentity(identity: UserIdentity | null) {
    currentIdentity = identity;
    const app = effectiveApp();
    if (!identity || !app) return;
    if (sessionStorage.getItem(SESSION_LOGIN_FLAG) === '1') return;

    void emit({
      userObjectId: identity.oid,
      userName: identity.name,
      userEmail: identity.email,
      app,
      eventType: 'login',
      path: currentPath(),
      sessionId: getSessionId(),
      queryParams: currentQuery(),
    });
    sessionStorage.setItem(SESSION_LOGIN_FLAG, '1');
  },

  /** Called on area/nav changes; throttles per area. */
  trackAreaVisit(area: string) {
    const app = effectiveApp();
    if (!currentIdentity || !app) return;
    if (!area) return;

    const now = Date.now();
    if (
      lastAreaEmit &&
      lastAreaEmit.area === area &&
      now - lastAreaEmit.at < AREA_THROTTLE_MS
    ) {
      return;
    }
    lastAreaEmit = { area, at: now };

    void emit({
      userObjectId: currentIdentity.oid,
      userName: currentIdentity.name,
      userEmail: currentIdentity.email,
      app,
      eventType: 'area_visit',
      area,
      path: currentPath(),
      sessionId: getSessionId(),
      queryParams: currentQuery(),
    });
  },

  /**
   * Called by AuthProvider on sign-out. Awaited so the event has a chance
   * to land before the window redirects.
   */
  async trackLogout() {
    const app = effectiveApp();
    if (!currentIdentity || !app) return;
    const identity = currentIdentity;
    try {
      await emit({
        userObjectId: identity.oid,
        userName: identity.name,
        userEmail: identity.email,
        app,
        eventType: 'logout',
        path: currentPath(),
        sessionId: getSessionId(),
      });
    } finally {
      sessionStorage.removeItem(SESSION_LOGIN_FLAG);
      sessionStorage.removeItem(SESSION_ID_KEY);
      currentIdentity = null;
      lastAreaEmit = null;
    }
  },
};
